import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { darkTheme, lightTheme, Theme } from '@edusense/shared-ui-kit';

// VSCode API 전역 타입 정의
declare global {
  interface Window {
    acquireVsCodeApi?: () => {
      postMessage: (message: any) => void;
      getState: () => any;
      setState: (state: any) => void;
    };
  }
}

// VSCode 웹뷰 API 타입 정의
interface VSCodeAPI {
  postMessage: (message: any) => void;
  getState: () => any;
  setState: (state: any) => void;
}

// VSCode 테마 타입
type VSCodeThemeInfo = {
  kind: 'light' | 'dark' | 'high-contrast';
  colors: Record<string, string>;
};

// VSCode API를 가져오는 함수
const getVSCodeAPI = (): VSCodeAPI | undefined => {
  try {
    return window.acquireVsCodeApi?.();
  } catch (e) {
    return undefined;
  }
};

// 테마 타입
type ThemeMode = 'light' | 'dark';

// 테마 컨텍스트 타입
interface ThemeContextType {
  themeMode: ThemeMode;
  theme: Theme;
  toggleTheme: () => void;
  vsCodeThemeInfo?: VSCodeThemeInfo;
}

// 테마 컨텍스트 생성
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 테마 컨텍스트 훅
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme은 ThemeProvider 내부에서만 사용할 수 있습니다.');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemeMode;
  isStorybook?: boolean;
  /**
   * VSCode 테마와 연동할지 여부
   * true인 경우 VSCode 테마가 변경되면 앱 테마도 자동으로 변경됩니다.
   */
  isVSCodeThemed?: boolean;
}

// 테마 프로바이더 컴포넌트
export const ThemeProvider = ({
  children,
  initialTheme = 'light',
  isStorybook = false,
  isVSCodeThemed = false
}: ThemeProviderProps) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialTheme);
  const [vsCodeThemeInfo, setVSCodeThemeInfo] = useState<VSCodeThemeInfo | undefined>(undefined);

  // isDark 속성 포함된 theme 객체 생성 (useMemo 사용)
  const theme = useMemo(() => ({
    ...(themeMode === 'light' ? lightTheme : darkTheme),
    isDark: themeMode === 'dark',
  }), [themeMode]);

  // 테마 전환 함수
  const toggleTheme = () => {
    setThemeMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // VSCode 테마 정보 요청
  useEffect(() => {
    if (!isVSCodeThemed) return;

    const vscodeApi = getVSCodeAPI();
    if (vscodeApi) {
      // VSCode에 테마 정보 요청 (새 형식)
      vscodeApi.postMessage({ messageType: 'request-theme' });
      // 이전 형식 지원 (하위 호환성)
      setTimeout(() => {
        vscodeApi.postMessage({ type: 'request-theme' });
      }, 500);
    }

    // VSCode로부터의 메시지 수신
    const handleMessage = (event: MessageEvent) => {
      const message = event.data;
      
      // 새 형식 (messageType)
      if (message.messageType === 'theme-changed') {
        const themeInfo = message.data as VSCodeThemeInfo;
        setVSCodeThemeInfo(themeInfo);
        // VSCode 테마에 따라 앱 테마 설정
        setThemeMode(themeInfo.kind === 'light' ? 'light' : 'dark');
      } 
      // 구 형식 (type)
      else if (message.type === 'theme-changed') {
        // VSCode 테마에 따라 앱 테마 설정
        setThemeMode(message.theme === 'light' ? 'light' : 'dark');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isVSCodeThemed]);

  // 시스템 테마 감지 및 적용 (스토리북 및 VSCode 테마 연동 모드 외)
  useEffect(() => {
    // 스토리북이나 VSCode 테마 연동 모드에서는 시스템 테마 감지 비활성화
    if (isStorybook || isVSCodeThemed) return;
    
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setThemeMode(prefersDark ? 'dark' : 'light');
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setThemeMode(e.matches ? 'dark' : 'light');
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isStorybook, isVSCodeThemed]);

  // HTML에 테마 클래스 및 CSS 변수 적용
  useEffect(() => {
    const root = document.documentElement;
    
    // 현재 테마에 기반하여 CSS 변수 설정
    const currentTheme = themeMode === 'light' ? lightTheme : darkTheme;

    // CSS 변수 설정 함수
    const setCSSVariables = (theme: Theme) => {
      // 트랜지션 비활성화 (변경 중에 깜빡임 방지)
      root.style.setProperty('transition', 'none');
      
      root.style.setProperty('--theme-bg', theme.background);
      root.style.setProperty('--theme-text', theme.text);
      root.style.setProperty('--theme-text-secondary', theme.textSecondary);
      root.style.setProperty('--theme-text-muted', theme.textMuted);
      root.style.setProperty('--theme-border', theme.border);
      root.style.setProperty('--theme-border-light', theme.borderLight);
      root.style.setProperty('--theme-paper', theme.paper);
      root.style.setProperty('--theme-paper-light', theme.paperLight);
      root.style.setProperty('--theme-paper-hover', theme.paperHover);
      root.style.setProperty('--theme-paper-active', theme.paperActive);
      root.style.setProperty('--theme-paper-dark', theme.paperDark);
      root.style.setProperty('--theme-primary', theme.primary);
      root.style.setProperty('--theme-primary-dark', theme.primaryDark);
      root.style.setProperty('--theme-primary-light', theme.primaryLight);
      root.style.setProperty('--theme-primary-text', theme.primaryText);
      root.style.setProperty('--theme-secondary', theme.secondary);
      root.style.setProperty('--theme-secondary-dark', theme.secondaryDark);
      root.style.setProperty('--theme-secondary-light', theme.secondaryLight);
      root.style.setProperty('--theme-secondary-text', theme.secondaryText);
      root.style.setProperty('--theme-shadow', theme.shadow);
      
      // 트랜지션 효과 설정
      root.style.setProperty('--theme-transition-fast', theme.transition.fast);
      root.style.setProperty('--theme-transition-normal', theme.transition.normal);
      root.style.setProperty('--theme-transition-slow', theme.transition.slow);
      
      // 약간의 지연 후 트랜지션 다시 활성화
      setTimeout(() => {
        root.style.setProperty('transition', '');
      }, 50);
    };
    
    if (themeMode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
      
      // 강제 스타일 적용
      document.body.style.backgroundColor = darkTheme.background;
      document.body.style.color = darkTheme.text;
      
      // 다크 테마용 CSS 변수 설정
      setCSSVariables(darkTheme);
      
      // 다크 모드 호버 변수
      root.style.setProperty('--theme-hover-bg', 'rgba(31, 41, 55, 0.6)');
      root.style.setProperty('--theme-hover-light', 'rgba(59, 130, 246, 0.1)');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
      
      // 강제 스타일 적용
      document.body.style.backgroundColor = lightTheme.background;
      document.body.style.color = lightTheme.text;
      
      // 라이트 테마용 CSS 변수 설정
      setCSSVariables(lightTheme);
      
      // 라이트 모드 호버 변수
      root.style.setProperty('--theme-hover-bg', 'rgba(249, 250, 251, 0.8)');
      root.style.setProperty('--theme-hover-light', 'rgba(59, 130, 246, 0.1)');
    }
  }, [themeMode]);

  // Emotion의 ThemeProvider만 사용
  return (
    <ThemeContext.Provider value={{ themeMode, theme, toggleTheme, vsCodeThemeInfo }}>
      <EmotionThemeProvider theme={theme}>
        {children}
      </EmotionThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 